import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Plus, Ship, Trash2 } from "lucide-react";

const money = (n) => (typeof n === "number" && Number.isFinite(n) ? `$${n.toFixed(2)}` : "—");

export default function DealerContainerOrder() {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState([]);
  const [lines, setLines] = useState([]);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/catalog?limit=500")
      .then((res) => setCatalog((res.data || []).filter((v) => v.pricing_eligible && v.active !== false)))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);

  const validLines = useMemo(
    () => lines.filter((l) => l.variant_id && parseInt(l.boxes, 10) > 0),
    [lines]
  );

  useEffect(() => {
    if (validLines.length === 0) { setQuote(null); return; }
    const t = setTimeout(async () => {
      setQuoting(true);
      try {
        const { data } = await api.post("/dealer/pricing/quote", {
          fulfillment_mode: "FULL_CONTAINER",
          lines: validLines.map((l) => ({
            variant_id: l.variant_id,
            boxes: parseInt(l.boxes, 10),
          })),
        });
        setQuote(data);
      } catch (e) {
        setQuote({ error: formatApiErrorDetail(e.response?.data?.detail) });
      } finally { setQuoting(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [validLines]);

  const addLine = () => setLines((ls) => [...ls, { variant_id: "", boxes: "", pallets: "" }]);
  const bppOf = (vid) => catalog.find((v) => v.id === vid)?.boxes_per_pallet || 0;
  // Boxes and pallets are two views of the SAME quantity (founder rule I3) — boxes are
  // the canonical value sent to the API; the pallets input only multiplies into boxes.
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, j) => {
    if (j !== i) return l;
    const next = { ...l, [k]: v };
    if (k === "pallets") next.boxes = v && bppOf(next.variant_id) ? String(parseInt(v, 10) * bppOf(next.variant_id)) : next.boxes;
    if (k === "boxes") next.pallets = "";
    return next;
  }));
  const quoteLine = (vid) => quote?.lines?.find((l) => l.variant_id === vid);

  const submit = async () => {
    setSubmitting(true);
    try {
      const idemKey = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `dc-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const { data } = await api.post("/orders", {
        fulfillment_mode: "FULL_CONTAINER",
        lines: validLines.map((l) => ({
          variant_id: l.variant_id,
          boxes: parseInt(l.boxes, 10),
          ...(l.pallets ? { pallets: parseInt(l.pallets, 10) } : {}),
        })),
      }, { headers: { "Idempotency-Key": idemKey } });
      toast.success("Full Container order created — 30% deposit due to confirm");
      navigate(`/dealer/orders/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const c = quote?.container;
  return (
    <div data-testid="container-order-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Direct from the atelier</p>
      <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Full Container order</h1>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-warmgrey">
        Mix any standard formats — the complete load qualifies, not each line. Freight &amp; logistics are quoted separately and confirmed by Zelligio before payment.
      </p>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border-border bg-white lg:col-span-2">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Lines</p>
              <Button variant="outline" size="sm" onClick={addLine} data-testid="add-container-line" className="border-limestone text-charcoal hover:bg-sand">
                <Plus className="mr-1 h-3.5 w-3.5" /> Add line
              </Button>
            </div>
            <div className="mt-4 space-y-3">
              {lines.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground" data-testid="container-lines-empty">Add your first line to start building the container.</p>}
              {lines.map((l, i) => {
                const q = quoteLine(l.variant_id);
                return (
                  <div key={i} className={`flex flex-wrap items-end gap-3 rounded-md border p-4 transition-colors ${q ? "border-status-available/50 bg-status-available/5" : "border-border bg-background"}`} data-testid={`container-line-${i}`}>
                    <div className="min-w-[220px] flex-1">
                      <Select value={l.variant_id} onValueChange={(v) => setLine(i, "variant_id", v)}>
                        <SelectTrigger data-testid={`container-variant-${i}`} className="h-11 bg-white"><SelectValue placeholder="Select variant" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {catalog.map((v) => <SelectItem key={v.id} value={v.id}>{v.label || `${v.color_name} ${v.format_in}`} · {v.sku}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-24">
                      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Boxes</p>
                      <Input type="number" min="1" placeholder="Boxes" value={l.boxes} onChange={(e) => setLine(i, "boxes", e.target.value)} data-testid={`container-boxes-${i}`} className="h-11 bg-white focus-visible:ring-terracotta" />
                    </div>
                    <div className="w-28">
                      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Pallets{bppOf(l.variant_id) ? ` (${bppOf(l.variant_id)}/plt)` : ""}</p>
                      <Input type="number" min="1" placeholder="Pallets" value={l.pallets} onChange={(e) => setLine(i, "pallets", e.target.value)} data-testid={`container-pallets-${i}`} className="h-11 bg-white focus-visible:ring-terracotta" />
                    </div>
                    <div className="w-28 text-right text-sm text-charcoal" data-testid={`container-line-price-${i}`}>
                      {q ? `${money(q.price_per_box)}/box` : "—"}
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} data-testid={`container-remove-${i}`}><Trash2 className="h-4 w-4 text-status-sold" /></Button>
                    {q && (
                      <p className="w-full text-xs font-medium text-status-available" data-testid={`container-line-confirmed-${i}`}>
                        ✓ Added to container — {parseInt(l.boxes, 10)} boxes{bppOf(l.variant_id) ? ` = ${(parseInt(l.boxes, 10) / bppOf(l.variant_id)) % 1 === 0 ? parseInt(l.boxes, 10) / bppOf(l.variant_id) : (parseInt(l.boxes, 10) / bppOf(l.variant_id)).toFixed(1)} pallet(s)` : ""}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="border-border bg-white" data-testid="container-summary">
            <CardContent className="p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Container qualification</p>
              {quoting && <p className="mt-3 text-xs text-muted-foreground"><Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> Pricing…</p>}
              {quote?.error && <p className="mt-3 text-sm text-status-sold" data-testid="container-quote-error">{quote.error}</p>}
              {c && (
                <>
                  <p className="mt-3 font-display text-2xl font-light text-charcoal" data-testid="container-progress">
                    {c.positions} of {c.positions_required} pallet positions
                  </p>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-sand">
                    <div className="h-full bg-terracotta transition-all" style={{ width: `${Math.min(100, (c.positions / c.positions_required) * 100)}%` }} />
                  </div>
                  <p className="mt-2 text-xs text-warmgrey" data-testid="container-qualification-message">
                    {c.qualified ? `✓ Full container qualified — ${c.positions} / ${c.positions_required} pallet positions` : `${c.positions_required - c.positions} pallet position(s) remaining to qualify for Full Container Pricing`}
                  </p>
                  <div className="mt-4 space-y-1 border-t border-border pt-4 text-sm">
                    <p className="flex justify-between"><span className="text-muted-foreground">Product subtotal</span><span className="text-charcoal" data-testid="container-subtotal">{money(quote.subtotal_usd)}</span></p>
                    <p className="flex justify-between"><span className="text-muted-foreground">Freight & logistics (estimate)</span><span className="text-charcoal" data-testid="container-freight">{money(c.freight_estimate_usd)}</span></p>
                    <p className="flex justify-between border-t border-border pt-1 font-medium"><span className="text-charcoal">Estimated total</span><span className="text-charcoal" data-testid="container-grand-total">{money(quote.grand_total_estimate_usd)}</span></p>
                  </div>
                  <p className="mt-2 text-[11px] text-muted-foreground">{c.freight_note}</p>
                </>
              )}
            </CardContent>
          </Card>
          <Button onClick={submit} disabled={submitting || !c?.qualified} data-testid="container-order-submit" className="h-12 w-full bg-terracotta text-white hover:bg-terracotta-hover">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Ship className="mr-2 h-4 w-4" /> Place Full Container order</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
